import React, { useMemo } from "react";
import { useLocation } from "react-router";
import { useSelector } from "react-redux";

import { navigation } from "src/config/supportNavigation";
import { supportIsAdmin } from "src/store/support/selector";
import LOGO from "src/assets/svgs/logo.png";
import EMBLEM_LOGO from "src/assets/svgs/Emblem_Logo.svg";

import {
  Box,
  Drawer,
  DrawerProps,
  List,
  ListItem,
  makeStyles,
} from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import NavigatorItem from "./NavigatorItem";
import type { Theme } from "src/theme";

interface NavigatorOwnProps {
  title: string;
  subTitle: string;
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

interface NavigatorDispatchProps {
  updateUserChecklist: (update: any) => void;
}

export type NavigatorProps = NavigatorOwnProps &
  DrawerProps &
  NavigatorDispatchProps &
  any;

const useStyles = makeStyles((theme: Theme) => {
  const isDark = theme.palette.type === "dark";
  const { custom: palette, derived } = theme.palette.custom;

  return {
    item: {
      backgroundColor: palette.card,
      paddingTop: 15,
      paddingBottom: 15,
      justifyContent: "center",
    },
    collapseTab: {
      position: "absolute",
      right: -16,
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 48,
      backgroundColor: palette.card,
      borderRadius: "0 8px 8px 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: isDark
        ? "2px 0 4px rgba(0,0,0,0.3)"
        : "2px 0 4px rgba(0,0,0,0.1)",
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
      zIndex: 1,
    },
    collapseTabIcon: {
      color: derived.accentOnCard,
      fontSize: 16,
    },
    emblemLogo: {
      height: 48,
      width: 48,
    },
    logo: {
      filter: isDark ? "brightness(0) invert(1)" : "none",
    },
  };
});

function Navigator({
  title,
  subTitle,
  variant,
  onClose,
  updateUserChecklist,
  staticContext,
  collapsed = false,
  onToggleCollapse,
  ...other
}: NavigatorProps) {
  const classes = useStyles();
  const location = useLocation();
  const isAdmin = useSelector(supportIsAdmin);
  const sorted = useMemo(() => {
    const sorted = navigation.filter((entry) => !entry.adminOnly || isAdmin);
    sorted.sort((a, b) => a.order - b.order);
    return sorted;
  }, [isAdmin]);

  return (
    <Drawer
      variant={variant}
      {...other}
      ModalProps={{ ...other?.ModalProps, onBackdropClick: onClose }}
    >
      {onToggleCollapse && (
        <Box className={classes.collapseTab} onClick={onToggleCollapse}>
          {collapsed ? (
            <ChevronRightIcon className={classes.collapseTabIcon} />
          ) : (
            <ChevronLeftIcon className={classes.collapseTabIcon} />
          )}
        </Box>
      )}
      <List disablePadding>
        <ListItem className={classes.item} key="1">
          {collapsed ? (
            <img
              src={EMBLEM_LOGO}
              alt={title}
              className={`${classes.emblemLogo} ${classes.logo}`}
            />
          ) : (
            <img src={LOGO} alt={title} className={`h-24 ${classes.logo}`} />
          )}
        </ListItem>
        <>
          {sorted.map(({ id: childId, Icon, title, href }) => {
            return (
              <NavigatorItem
                key={childId}
                primary={title}
                id={childId}
                Icon={Icon}
                to={href}
                active={location.pathname === href}
                collapsed={collapsed}
              />
            );
          })}
        </>
      </List>
    </Drawer>
  );
}

export default Navigator;
